import { Fragment } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { selectAuth } from "../redux/features/auth/auth-slice";
import { selectProfile } from "../redux/features/user/user-slice";
import DashboardLayout from "../shared/components/dashboard-layout/dashboard-layout";
import PublicRoute from "../shared/components/public-route/public-route";
import { EOrgUserRole } from "../shared/oversight-core/enums/org-user-role";
import { EPrivileges } from "../shared/oversight-core/enums/privileges";
import { UserRole } from "../shared/oversight-core/enums/user-roles";
import { AppRoute } from "../shared/oversight-core/interfaces/app-routes";
import IPageInfo from "../shared/oversight-core/interfaces/page-info";
import NotFound from "../shared/oversight-core/shared-components/not-found/not-found";
import PrivateRoute from "../shared/oversight-core/shared-components/private-route/private-route";
import Unauthorized from "../shared/oversight-core/shared-components/unauthorized/unauthorized";
import ForgotPassword from "../shared/oversight-core/shared-pages/forgot-password/forgot-password";
import MobileNumberOtpVerification from "../shared/oversight-core/shared-pages/mobile-number-otp-verification/mobile-number-otp-verification";
import ResetPassword from "../shared/oversight-core/shared-pages/reset-password/reset-password";
import UserProfile from "../shared/oversight-core/shared-pages/user-profile/user-profile";
import RemoveTrailingSlash from "../shared/oversight-core/utils/removeTrailingSlash";
import { isGuestRole } from "../shared/utils/org-roles";
import ClaimAccount from "./claim-account/claim-account";
import AdminDashboard from "./dashboard/dashboard";
import AdminLogin from "./login/login";
import AdminSettings from "./settings/settings";
import Sites from "./sites/sites";
import SitesErrorLog from "./sites/sites-error-log/sites-error-log";
import ViewSite from "./sites/view-site/view-site";
import Users from "./users/users";

// The platform admin and every organization role use the dashboard. What each one may see is
// decided by the backend; the guard only keeps out accounts that do not belong here.
const DASHBOARD_ROLES = [
  UserRole.ROLE_ADMIN,
  ...Object.values(EOrgUserRole),
] as string[] as UserRole[];

// Pages only the platform admin and an organization admin can use: managing users, and the
// organization's settings. Other roles are refused by the backend there, so they are not offered.
const ADMIN_ONLY_PATHS: string[] = [
  AppRoute.ADMIN_USERS,
  AppRoute.ADMIN_SETTINGS,
];
const ADMIN_ROLES: string[] = [UserRole.ROLE_ADMIN, UserRole.ROLE_ORG_ADMIN];

// The fleet error log is alerts and diagnostics, which a monitoring guest is not shown.
const GUEST_HIDDEN_PATHS: string[] = [AppRoute.ADMIN_SITES_ERROR_LOG];

export const isRouteAllowedForRole = (path: string, role?: string) =>
  (!ADMIN_ONLY_PATHS.includes(path) ||
    (!!role && ADMIN_ROLES.includes(role))) &&
  !(isGuestRole(role) && GUEST_HIDDEN_PATHS.includes(path));

// The routes as one role sees them, for the navigation drawer.
export const routesForRole = (routes: IPageInfo[], role?: string) =>
  routes.map((route) => ({
    ...route,
    subRoutes: route.subRoutes?.filter((subRoute) =>
      isRouteAllowedForRole(subRoute.path, role),
    ),
  }));

export const publicRoutes: IPageInfo[] = [
  {
    name: "Forgot Password",
    path: AppRoute.FORGOT_PASSWORD,
    component: <ForgotPassword />,
    enableRoute: true,
  },
  {
    name: "Reset Password",
    path: AppRoute.RESET_PASSWORD,
    component: <ResetPassword />,
    enableRoute: true,
  },
  {
    name: "Mobile Number Verification",
    path: AppRoute.MOBILE_NUMBER_VERIFICATION,
    component: <MobileNumberOtpVerification />,
    enableRoute: true,
  },
  {
    name: "Claim Account",
    // Invitation emails link here; the backend builds the address, so it is not in the shared AppRoute.
    path: "/claim-account",
    component: <ClaimAccount />,
    enableRoute: true,
  },
  {
    name: "Admin Login",
    path: AppRoute.ADMIN_LOGIN,
    component: <AdminLogin />,
    enableRoute: true,
  },
];

export const adminPrivateRoutes: IPageInfo[] = [
  {
    name: "Change Password",
    path: AppRoute.CHANGE_PASSWORD,
    component: <ResetPassword />,
    enableRoute: true,
  },
  {
    name: "Admin",
    path: AppRoute.ADMIN,
    component: <DashboardLayout />,
    root: true,
    subRoutes: [
      {
        name: "Dashboard",
        icon: "home",
        path: AppRoute.ADMIN_DASHBOARD,
        index: true,
        component: <AdminDashboard />,
        enableRoute: true,
        bgColor: "activeBg-1",
        bgColorIcon: "#1A3C84",
        showOnDrawer: true,
        privilege: EPrivileges.DASHBOARD_READ_PRIVILEGE,
      },
      {
        name: "Sites",
        icon: "sunny",
        path: AppRoute.ADMIN_SITES_ERROR_LOG,
        index: true,
        component: <SitesErrorLog />,
        enableRoute: true,
        bgColor: "activeBg-13",
        bgColorIcon: "#AC782B",
        showOnDrawer: false,
      },
      {
        name: "Sites",
        icon: "sunny",
        path: AppRoute.ADMIN_SITES,
        index: true,
        component: <Sites />,
        enableRoute: true,
        bgColor: "activeBg-13",
        bgColorIcon: "#AC782B",
        showOnDrawer: true,
      },
      {
        name: "Sites",
        icon: "sunny",
        path: AppRoute.ADMIN_SITES + "/:id",
        index: true,
        component: <ViewSite />,
        enableRoute: true,
        bgColor: "activeBg-13",
        bgColorIcon: "#AC782B",
        showOnDrawer: false,
      },
      {
        name: "Users",
        icon: "person_outline",
        path: AppRoute.ADMIN_USERS,
        component: <Users />,
        enableRoute: true,
        bgColor: "activeBg-1",
        bgColorIcon: "#1A3C84",
        showOnDrawer: true,
      },
      {
        name: "Settings",
        icon: "settings",
        path: AppRoute.ADMIN_SETTINGS,
        component: <AdminSettings />,
        enableRoute: true,
        bgColor: "activeBg-1",
        bgColorIcon: "#1A3C84",
        showOnDrawer: true,
      },
      {
        name: "Profile",
        path: AppRoute.ADMIN_PROFILE,
        component: <UserProfile />,
        enableRoute: true,
        showOnDrawer: false,
        privilege: EPrivileges.GENERAL_READ_PRIVILEGE,
      },
      {
        name: "404 Page Not Found",
        path: AppRoute.ADMIN_NOT_FOUND,
        component: <NotFound />,
        showOnDrawer: false,
      },
    ],
  },
];

const AppRoutes = () => {
  const auth = useSelector(selectAuth);
  const profile = useSelector(selectProfile);
  const getRoutes = (routes: IPageInfo[]) => {
    return routes.map((privateRoutes: IPageInfo, index) => (
      <Route
        key={index}
        path={privateRoutes.path}
        element={privateRoutes.component}
      >
        {privateRoutes.subRoutes?.map((subRoute: IPageInfo, index) => {
          const route = [
            <Route
              key={index}
              path={subRoute.path}
              element={
                (!subRoute.privilege ||
                  auth.privileges.includes(subRoute.privilege)) &&
                isRouteAllowedForRole(subRoute.path, profile?.role) ? (
                  subRoute.component
                ) : (
                  <Unauthorized />
                )
              }
            />,
          ];

          if (subRoute.index) {
            route.push(
              <Route
                key={index + subRoute.path}
                index
                element={<Navigate to={subRoute.path} />}
              />,
            );
          }
          return route;
        })}
      </Route>
    ));
  };

  return (
    <Fragment>
      <RemoveTrailingSlash />
      <Routes>
        <Route path="/" element={<PublicRoute />}>
          <Route index element={<Navigate to={AppRoute.ADMIN_LOGIN} />} />
          {publicRoutes.map((p: IPageInfo, index) => {
            return <Route key={index} path={p.path} element={p.component} />;
          })}
          <Route path="*" element={<Navigate to={AppRoute.ADMIN_LOGIN} />} />
        </Route>

        <Route path="/" element={<PrivateRoute role={DASHBOARD_ROLES} />}>
          {getRoutes(adminPrivateRoutes)}
        </Route>

        <Route path="/unauthorized" element={<Unauthorized />} />
      </Routes>
    </Fragment>
  );
};

export default AppRoutes;
